import { gridDateComparator } from '@mui/x-data-grid-pro';
import { CuriosityNamespaceType } from '@procureai/curiosity-ui-mui';
import { Permission } from '@procureai/permissions';
import { DateFormatPreset } from '@procureai/ui-mui';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { SvbrgChatSession } from '@/generated/gql/graphql';
import { I18nNamespace } from '~/common/types';
import {
  DataGridConfig,
  DataGridLinkVariant,
  LinkCell,
  LinkCellValue,
  NULLISH_CELL_VALUE,
  TooltipCell,
  UseDataGridConfig,
  cellFactory,
  dateFormatterFactory,
  gridColDefFactory,
  gridColumnGroupFactory,
  headerGroupWithIcon,
} from '~/components/DataGridCustom';
import { CuriosityValueCell } from '~/components/DataGridCustom/cells/CuriosityValueCell/CuriosityValueCell';
import { IntakeStatusChipCell } from '~/components/DataGridCustom/cells/IntakeStatusChipCell/IntakeStatusChipCell';
import { UserChipCell } from '~/components/DataGridCustom/cells/UserChipCell/UserChipCell';
import { IconId } from '~/components/Icon';
import { RouteId } from '~/routes/RouteId';

import { IntakeGridAction } from './components/IntakeGridAction/IntakeGridAction';
import { IntakeListDataGridColumn, IntakeListDataGridColumnGroup } from './IntakeListDataGrid.types';

export const useIntakeListDataGridConfig: UseDataGridConfig<SvbrgChatSession> = () => {
  const { t } = useTranslation();
  const { t: tColumn } = useTranslation(I18nNamespace.intake, { keyPrefix: 'IntakeListDataGrid.column' });
  const { t: tGroup } = useTranslation(I18nNamespace.intake, { keyPrefix: 'IntakeListDataGrid.group' });

  return useMemo(() => {
    const createColumn = gridColDefFactory({ tColumn, t, defaultOptions: { sortable: false } });
    const createGroup = gridColumnGroupFactory({ tGroup });
    return {
      sortModel: [{ field: IntakeListDataGridColumn.SEQUENCE_NUMBER, sort: 'desc' }],
      columnVisibilityModel: {},
      columns: [
        createColumn({
          field: IntakeListDataGridColumn.SEQUENCE_NUMBER,
          minWidth: 100,
          sortable: true,
        }),
        createColumn({
          field: IntakeListDataGridColumn.CREATED_AT,
          valueFormatter: dateFormatterFactory({ preset: DateFormatPreset.TWO_DIGIT }),
          sortComparator: gridDateComparator,
          minWidth: 150,
          sortable: true,
        }),
        createColumn({
          field: IntakeListDataGridColumn.STATUS,
          minWidth: 150,
          renderCell: cellFactory(IntakeStatusChipCell),
          sortable: true,
        }),
        createColumn({
          field: IntakeListDataGridColumn.SUMMARY,
          minWidth: 250,
          renderCell: cellFactory(TooltipCell),
        }),
        createColumn({
          field: IntakeListDataGridColumn.CREATED_BY,
          minWidth: 150,
          renderCell: cellFactory(CuriosityValueCell, {
            ns: CuriosityNamespaceType.INTERNAL_USER,
          }),
        }),
        createColumn({
          field: IntakeListDataGridColumn.DEMAND_ID,
          renderCell: cellFactory(LinkCell, { needs: { permission: Permission.DemandList } }),
          valueGetter: (_value, row): LinkCellValue => {
            return {
              variant: DataGridLinkVariant.STANDARD,
              to: row.demandId ? RouteId.demandDetail(row.demandId) : '',
              label: row.demandSequenceNumber ?? NULLISH_CELL_VALUE,
            };
          },
          valueFormatter: (value: { label: string }) => value?.label,
          minWidth: 200,
          sortable: true,
        }),
        createColumn({
          field: IntakeListDataGridColumn.DEMAND_RESPONSIBLE_USERS,
          renderCell: cellFactory(UserChipCell),
          minWidth: 200,
        }),
        createColumn({
          field: IntakeListDataGridColumn.ACTIONS,
          width: 64,
          minWidth: 64,
          maxWidth: 64,
          type: 'actions',
          headerName: '',
          sortable: false,
          align: 'center',
          disableExport: true,
          getActions: (params) => {
            const id = params.row.id;
            const status = params.row.status;
            if (!status) {
              return [];
            }

            return [<IntakeGridAction id={id} status={status} key={`action-${id}`} />];
          },
        }),
      ],
      columnGroupingModel: [
        createGroup({
          groupId: IntakeListDataGridColumnGroup.PURCHASE_REQUISITION,
          renderHeaderGroup: headerGroupWithIcon(IconId.demand),
          children: [
            //
            { field: IntakeListDataGridColumn.SEQUENCE_NUMBER },
            { field: IntakeListDataGridColumn.CREATED_AT },
            { field: IntakeListDataGridColumn.STATUS },
            { field: IntakeListDataGridColumn.SUMMARY },
            { field: IntakeListDataGridColumn.CREATED_BY },
          ],
        }),
        createGroup({
          groupId: IntakeListDataGridColumnGroup.DEMAND,
          renderHeaderGroup: headerGroupWithIcon(IconId.demand),
          children: [
            //
            { field: IntakeListDataGridColumn.DEMAND_ID },
            { field: IntakeListDataGridColumn.DEMAND_RESPONSIBLE_USERS },
          ],
        }),
      ],
      exportModel: {},
    } as DataGridConfig<SvbrgChatSession>;
  }, [t, tColumn, tGroup]);
};
